import { isAppleDevice } from './detectDevice';
import { SMS_AUTH_ALLOWED_COUNTRIES } from './phone';

export const ONBOARDING_VARIANT_PARAM = 'onboardingVariant';

// Turn this on at launch only after analytics can segment locale-eligible SMS
// exposure from ineligible exposure; it enables SMS for eligible locales.
export const SMS_SIGNUP_LAUNCH_ENABLED = false;

const getHashQuery = (hash: string): string =>
  hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';

export const getOnboardingVariant = (
  search: string,
  hash: string
): string | null => {
  const hashQuery = getHashQuery(hash);
  return (
    new URLSearchParams(search).get(ONBOARDING_VARIANT_PARAM) ??
    new URLSearchParams(hashQuery).get(ONBOARDING_VARIANT_PARAM)
  );
};

// The layer parameter is typed `unknown` by the provider, so the set is keyed
// on `unknown` to compare it without casting away the provider's contract.
export const NEW_REGISTER_DESIGN_LAYER_VARIANTS: ReadonlySet<unknown> = new Set(
  [
    'dynamic_3p',
    'dynamic_1p',
    'email_first',
    'loop_pre_auth',
    'loop_post_auth',
    'expression_pre_auth',
    'expression_post_auth',
    'expression_drop_in',
  ]
);

export const isTreatmentOnboardingSession = (
  search: string,
  hash: string
): boolean =>
  NEW_REGISTER_DESIGN_LAYER_VARIANTS.has(getOnboardingVariant(search, hash));

export const isNewRegisterSessionRandomizable = (
  isMobileAppSession: boolean
): boolean => !isMobileAppSession;

export const isRandomizableTreatmentOnboardingSession = (
  search: string,
  hash: string,
  isMobileAppSession: boolean
): boolean =>
  isTreatmentOnboardingSession(search, hash) &&
  isNewRegisterSessionRandomizable(isMobileAppSession);

export type SocialProvider = 'google' | 'apple' | 'email' | 'sms';

export const getLocaleRegion = (
  language?: string,
  languages: readonly string[] = []
): string | undefined => {
  for (const locale of [language, ...languages]) {
    if (!locale) {
      continue;
    }
    const match = locale.match(
      /^[A-Za-z]{2,3}(?:[-_][A-Za-z]{4})?[-_]([A-Za-z]{2}|\d{3})(?:$|[-_])/u
    );
    const region = match?.[1];
    if (region) {
      return region.toUpperCase();
    }
  }
  return undefined;
};

export const isSmsSignupLocaleEligible = (
  language?: string,
  languages: readonly string[] = []
): boolean => {
  const region = getLocaleRegion(language, languages);
  return Boolean(
    region && SMS_AUTH_ALLOWED_COUNTRIES.some(country => country === region)
  );
};

export const isSmsSignupVisible = (
  isQaEnabled: boolean,
  isLocaleEligible: boolean,
  isLaunchEnabled: boolean
): boolean => isQaEnabled || (isLaunchEnabled && isLocaleEligible);

const isMobileUserAgent = (userAgent: string): boolean =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);

const isAppleMobileUserAgent = (userAgent: string): boolean =>
  /iPhone|iPad|iPod/i.test(userAgent);

const isThirdPartyBrowser = (userAgent: string): boolean =>
  /Chrome|CriOS|Edg|EdgiOS|OPR|Opera|Firefox|FxiOS/i.test(userAgent);

const isAppleFirstBrowser = (userAgent: string): boolean => {
  if (!isAppleDevice(userAgent) || isThirdPartyBrowser(userAgent)) {
    return false;
  }

  // iOS WebViews omit browser tokens, so a non-third-party Apple mobile UA is
  // WebKit. macOS requires a positive Safari token to exclude Firefox-like UAs.
  if (isAppleMobileUserAgent(userAgent)) {
    return true;
  }

  return /Safari/i.test(userAgent);
};

export const getNewRegisterSocialOrder = (
  userAgent: string,
  variant?: string | null
): SocialProvider[] => {
  const thirdParty = isAppleFirstBrowser(userAgent)
    ? (['apple', 'google'] as SocialProvider[])
    : (['google', 'apple'] as SocialProvider[]);
  if (variant === 'email_first') {
    return ['email', 'sms', 'google', 'apple'].filter(
      provider => provider !== 'apple' || isAppleDevice(userAgent)
    ) as SocialProvider[];
  }
  if (variant === 'dynamic_1p') {
    return (
      isMobileUserAgent(userAgent)
        ? ['sms', 'email', 'google', 'apple']
        : ['email', 'sms', 'google', 'apple']
    ).filter(
      provider => provider !== 'apple' || isAppleDevice(userAgent)
    ) as SocialProvider[];
  }
  return [...thirdParty, 'email', 'sms'].filter(
    provider => provider !== 'apple' || isAppleDevice(userAgent)
  ) as SocialProvider[];
};
